import * as React from 'react';
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    Chip,
    Alert,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { centered_flex_box, main_button, sec_button } from '../../app/components/Styles';
import { getStockRecountTask, downloadStockRecountReceipt } from '../../app/store/actions/imsAdminActions.js';
import { Warning, Home, Replay, List, Print } from '@mui/icons-material';
import { useEffect, useCallback } from 'react';

const STATUS_COLORS = {
    pending: 'warning',
    processing: 'warning',
    success: 'success',
    failed: 'error',
    skipped: 'default',
};

const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
};

export const StockRecountProgress = ({ getStockRecountTask, downloadStockRecountReceipt }) => {
    const navigate = useNavigate();
    const { taskId } = useParams();
    const [task, setTask] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [pollError, setPollError] = React.useState(null);

    const fetchTask = useCallback(async () => {
        try {
            const data = await getStockRecountTask(taskId);
            setTask(data);
            setPollError(null);
        } catch (err) {
            setPollError(err?.response?.data?.message || 'Failed to load recount progress.');
        } finally {
            setIsLoading(false);
        }
    }, [getStockRecountTask, taskId]);

    useEffect(() => {
        fetchTask();
    }, [fetchTask]);

    useEffect(() => {
        if (!task || task.status !== 'processing') {
            return undefined;
        }

        const interval = setInterval(() => {
            fetchTask();
        }, 10000);

        return () => clearInterval(interval);
    }, [task?.status, fetchTask]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (task?.status === 'processing') {
                event.preventDefault();
                event.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [task?.status]);

    const summary = task?.summary || {};
    const items = task?.items || [];
    const completedCount = (summary.success || 0) + (summary.failed || 0) + (summary.skipped || 0);
    const progressPercent = summary.total > 0 ? Math.round((completedCount / summary.total) * 100) : 0;
    const isProcessing = task?.status === 'processing';

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Typography variant="h4" textAlign="center" sx={{ color: 'var(--secColor)', mb: 1 }}>
                    Stock Recount Progress
                </Typography>
                {task?.vendor?.brandName && (
                    <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 1 }}>
                        Brand: {task.vendor.brandName}
                    </Typography>
                )}
                {task?.admin?.name && (
                    <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
                        Started by {task.admin.name} · {task.createdAt ? new Date(task.createdAt).toLocaleString() : ''}
                    </Typography>
                )}

                {isProcessing && (
                    <Alert
                        severity="warning"
                        icon={<Warning />}
                        sx={{ mb: 3 }}
                    >
                        Do not close this page until the recount finishes. Updates are running in the background and may take several minutes due to Shopify rate limits.
                    </Alert>
                )}

                {task?.status === 'completed' && (
                    <Alert severity={summary.failed > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
                        Recount completed. {summary.success} succeeded, {summary.failed} failed, {summary.skipped} skipped.
                    </Alert>
                )}

                {task?.status === 'failed' && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        The recount task failed unexpectedly. Some products may not have been updated.
                    </Alert>
                )}

                {pollError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {pollError} — will retry on next poll.
                    </Alert>
                )}

                {isLoading && !task ? (
                    <Box sx={{ ...centered_flex_box, py: 6 }}>
                        <CircularProgress sx={{ color: 'var(--secColor)' }} />
                    </Box>
                ) : task ? (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={`Total: ${summary.total || 0}`} />
                            <Chip label={`Success: ${summary.success || 0}`} color="success" />
                            <Chip label={`Failed: ${summary.failed || 0}`} color="error" />
                            <Chip label={`Skipped: ${summary.skipped || 0}`} />
                            <Chip label={`Pending: ${summary.pending || 0}`} color="warning" />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Overall progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {progressPercent}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant={isProcessing ? 'determinate' : 'determinate'}
                                value={progressPercent}
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                            {isProcessing && (
                                <Box sx={{ ...centered_flex_box, mt: 2, gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: 'var(--secColor)' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Processing products...
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>SKU</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">On Hand Before</TableCell>
                                        <TableCell align="right">Actual Count</TableCell>
                                        <TableCell align="right">Target Stock</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow
                                            key={item.product?.toString?.() || item.sku}
                                            sx={{
                                                bgcolor: item.status === 'failed'
                                                    ? 'error.light'
                                                    : item.status === 'success'
                                                        ? 'success.light'
                                                        : item.status === 'processing'
                                                            ? 'warning.light'
                                                            : 'inherit',
                                            }}
                                        >
                                            <TableCell>{item.sku}</TableCell>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell align="right">{item.onHandBefore}</TableCell>
                                            <TableCell align="right">{item.actualCount}</TableCell>
                                            <TableCell align="right">{item.targetStockAmount}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatStatus(item.status)}
                                                    color={STATUS_COLORS[item.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="error">
                                                    {item.error || ''}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ ...centered_flex_box, gap: 2, mt: 3, flexWrap: 'wrap' }}>
                            <Button
                                sx={{ ...main_button }}
                                startIcon={<Print />}
                                onClick={() => downloadStockRecountReceipt(taskId)}
                            >
                                Print receipt
                            </Button>
                        {!isProcessing && (
                            <>
                                <Button
                                    sx={{ ...sec_button }}
                                    startIcon={<List />}
                                    onClick={() => navigate('/ims/admin/stock-recount/history')}
                                >
                                    Back to history
                                </Button>
                                <Button
                                    sx={{ ...sec_button }}
                                    startIcon={<Replay />}
                                    onClick={() => navigate('/ims/admin/stock-recount')}
                                >
                                    New Recount
                                </Button>
                                <Button
                                    sx={{ ...main_button }}
                                    startIcon={<Home />}
                                    onClick={() => navigate('/ims/admin')}
                                >
                                    Back to Dashboard
                                </Button>
                            </>
                        )}
                        </Box>
                    </>
                ) : null}
            </Paper>
        </Container>
    );
};

const mapDispatchToProps = { getStockRecountTask, downloadStockRecountReceipt };

export default connect(null, mapDispatchToProps)(StockRecountProgress);
